import { Extension, generateJSON } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import sanitizeHtml from "sanitize-html";
import { markdownToHtml } from "../utils/markdown-to-html";

const sanitizeHtmlFromClipboard = (htmlFromClipboard: string) => {
  console.log("htmlFromClipboard", htmlFromClipboard);
  return sanitizeHtml(htmlFromClipboard, {
    allowedTags: [
      "br",
      "p",
      "strong",
      "em",
      "u",
      "a",
      "img",
      "ul",
      "li",
      "ol",
      "s",
    ],
    allowedAttributes: {
      a: ["href"],
      img: ["src", "alt"],
    },
    allowedSchemes: ["http", "https", "data"],
    disallowedTagsMode: "discard",
    transformTags: {
      "*": (tagName, attribs) => {
        delete attribs.style;
        delete attribs.class;
        delete attribs.id;
        return { tagName, attribs };
      },
    },
    exclusiveFilter(frame) {
      if (frame.tag === "img") {
        return frame.attribs?.alt?.toLowerCase() !== "emoji";
      }
      return false;
    },
  });
};

const stripEmptyParagraphs = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.querySelectorAll("p").forEach((p) => {
    const content = p.innerHTML.trim();

    if (!content || content === "<br>" || content === "<br/>") {
      p.remove();
    }
  });

  return doc.body.innerHTML;
};

export const EventHandler = Extension.create({
  name: "eventHandler",

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: new PluginKey("eventHandler"),
        props: {
          handlePaste(view, event: ClipboardEvent) {
            const clipboardData = event.clipboardData;

            if (!clipboardData) return true;

            // If clipboard contains image files, let the image paste plugin handle it
            const items = clipboardData.items;
            if (
              items &&
              Array.from(items).some((i) => i.type.startsWith("image/"))
            ) {
              return true;
            }

            const htmlFromClipboard = clipboardData.getData("text/html") || "";
            const markdownFromClipboard =
              clipboardData.getData("text/markdown") ||
              clipboardData.getData("text/plain") ||
              "";

            let htmlToInsert = "";
            if (htmlFromClipboard.trim()) {
              // Sanitize and keep only allowed tags
              htmlToInsert = sanitizeHtmlFromClipboard(htmlFromClipboard);
            } else if (markdownFromClipboard.trim()) {
              // Convert Markdown (with mentions/emojis support) to HTML
              htmlToInsert = markdownToHtml(markdownFromClipboard);
            } else {
              return true;
            }

            htmlToInsert = stripEmptyParagraphs(htmlToInsert);

            if (!htmlToInsert.trim()) return true;

            editor.commands.insertContent(
              generateJSON(htmlToInsert, [
                // Resolve the editor's base graph once so composite extensions do not expand twice.
                ...editor.extensionManager.baseExtensions,
              ]),
              {
                parseOptions: {
                  preserveWhitespace: false,
                },
              },
            );

            return true;
          },
        },
      }),
    ];
  },
});
